import type { DragEvent } from 'react';

import type { IztContentNode } from '../../api/types';
import { contentFilterMatches, contentKind, contentTypeLabel, normalizeContentType, type ContentFilter } from '../../config/content-types';
import { ClipboardIcon, FileIcon, FolderIcon, SlideIcon, VideoIcon } from '../../layout/icons';
import { GENERATED_QUIZ_PREFIX } from '../filmstrip/documents';
import { ClipboardCountIcon, KebabIcon } from './panelGlyphs';
import { courseTotals, unitFactLine, unitSubtitle } from './lectureFacts';
import { useHorizontalOverflow } from './useHorizontalOverflow';

type ContentUnit = IztContentNode['sections'][number]['units'][number];

export interface LectureLibrarySelection {
  unit: ContentUnit;
  courseId?: string;
}

export function isBoardReady(unit: ContentUnit): boolean {
  const norm = normalizeContentType(unit.type);
  // exam-backed units paint their questions; homework opens its PDF
  return norm === 'pdf' || norm === 'exercise' || norm === 'homework' || contentKind(unit.type) === 'assessment' || unit.id.indexOf(GENERATED_QUIZ_PREFIX) === 0;
}

export const LECTURE_DRAG_TYPE = 'application/x-izt-unit';

/** Every unit in the tree matching the type filter and an already-lowercased query. */
export function flattenUnits(tree: IztContentNode[], filter: ContentFilter, query: string): LectureLibrarySelection[] {
  const rows: LectureLibrarySelection[] = [];
  tree.forEach((course) => course.sections.forEach((section) => section.units.forEach((unit) => {
    if (!contentFilterMatches(filter, unit.type)) return;
    if (query && unit.name.toLocaleLowerCase('vi-VN').indexOf(query) < 0) return;
    rows.push({ unit, courseId: course.id });
  })));
  return rows;
}

export interface RowHandlers {
  activeUnitId?: string | null;
  onSelect: (selection: LectureLibrarySelection) => void;
  onDragChange?: (selection: LectureLibrarySelection | null) => void;
}

function dragProps(row: LectureLibrarySelection, handlers: RowHandlers) {
  if (!handlers.onDragChange || !isBoardReady(row.unit)) return {};
  return {
    draggable: true,
    onDragStart: (event: DragEvent<HTMLElement>) => {
      event.dataTransfer.effectAllowed = 'copy';
      event.dataTransfer.setData(LECTURE_DRAG_TYPE, row.unit.id);
      event.dataTransfer.setData('text/plain', row.unit.name);
      if (handlers.onDragChange) handlers.onDragChange(row);
    },
    onDragEnd: () => { if (handlers.onDragChange) handlers.onDragChange(null); },
  };
}

function UnitTile({ unit }: { unit: ContentUnit }) {
  const kind = contentKind(unit.type);
  return (
    <span className={`lib-row__tile lib-row__tile--${kind}`} aria-hidden={true}>
      {kind === 'video' ? <VideoIcon size={18} /> : null}
      {kind === 'slide' ? <SlideIcon size={18} /> : null}
      {kind === 'assessment' ? <ClipboardIcon size={18} /> : null}
      {kind === 'other' ? <FileIcon size={18} /> : null}
    </span>
  );
}

export interface UnitListProps extends RowHandlers {
  rows: LectureLibrarySelection[];
}

/** Default 60px row - icon tile, raw title (the source ordinal is part of the design), server facts. */
export function UnitList({ rows, activeUnitId, onSelect, onDragChange }: UnitListProps) {
  return (
    <div className="lib-list">
      {rows.map((row) => {
        const selected = Boolean(activeUnitId) && row.unit.id === activeUnitId;
        return (
          <button
            type="button"
            key={`${row.courseId || ''}-${row.unit.id}`}
            className={selected ? 'lib-row is-selected' : 'lib-row'}
            aria-current={selected ? 'true' : undefined}
            onClick={() => onSelect(row)}
            {...dragProps(row, { activeUnitId, onSelect, onDragChange })}
          >
            <UnitTile unit={row.unit} />
            <span className="lib-row__main">
              <span className="lib-row__name" title={row.unit.name}>{row.unit.name}</span>
              <span className="lib-row__meta">{unitSubtitle(row.unit)}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

export interface SearchHitListProps extends RowHandlers {
  rows: LectureLibrarySelection[];
  courseName: (courseId?: string) => string;
}

/** Search rows are the compact 52px variant with a text badge instead of a glyph. */
export function SearchHitList({ rows, courseName, activeUnitId, onSelect, onDragChange }: SearchHitListProps) {
  return (
    <div className="lib-list">
      {rows.map((row) => {
        const kind = contentKind(row.unit.type);
        const selected = Boolean(activeUnitId) && row.unit.id === activeUnitId;
        // no publish date exists on the tree, so the second line carries real media facts or nothing
        const facts = unitFactLine(row.unit);
        return (
          <button
            type="button"
            key={`${row.courseId || ''}-${row.unit.id}`}
            className={selected ? 'lib-hit is-selected' : 'lib-hit'}
            aria-current={selected ? 'true' : undefined}
            onClick={() => onSelect(row)}
            {...dragProps(row, { activeUnitId, onSelect, onDragChange })}
          >
            <span className={`lib-hit__tile lib-hit__tile--${kind}`} aria-hidden={true}>{contentTypeLabel(row.unit.type)}</span>
            <span className="lib-hit__main">
              <span className="lib-hit__name" title={row.unit.name}>{row.unit.name}</span>
              <span className="lib-hit__meta">
                <span className="lib-hit__course">{courseName(row.courseId)}</span>
                {facts ? <span className="lib-hit__dot" aria-hidden={true} /> : null}
                {facts ? <span className="lib-hit__fact">{facts}</span> : null}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

export interface CourseStripProps {
  courses: IztContentNode[];
  onOpen: (course: IztContentNode) => void;
}

export function CourseStrip({ courses, onOpen }: CourseStripProps) {
  const { ref, faded, measure } = useHorizontalOverflow(courses.length);
  return (
    <div className="h-scroll lib-courses">
      <div className="h-scroll__track" ref={ref} onScroll={measure}>
        {courses.map((course) => {
          const totals = courseTotals(course);
          return (
            <button type="button" key={course.id} className="lib-course" onClick={() => onOpen(course)}>
              <span className="lib-course__cover">
                <span className="lib-course__glyph" aria-hidden={true}><FolderIcon size={44} /></span>
                <span className="lib-course__kebab" aria-hidden={true}><KebabIcon /></span>
              </span>
              <span className="lib-course__body">
                <span className="lib-course__name" title={course.name}>{course.name}</span>
                <span className="lib-course__count"><ClipboardCountIcon />{totals.chapters} chương • {totals.units} bài giảng</span>
              </span>
            </button>
          );
        })}
      </div>
      {faded ? <span className="h-scroll__fade" aria-hidden={true} /> : null}
    </div>
  );
}

export interface CourseDetailProps extends RowHandlers {
  course: IztContentNode;
}

export function CourseDetail({ course, activeUnitId, onSelect, onDragChange }: CourseDetailProps) {
  return (
    <div>
      {course.sections.map((section) => (
        <section key={section.id} className="lib-chapter">
          <h3 className="lib-chapter__title">{section.name}</h3>
          <UnitList rows={section.units.map((unit) => ({ unit, courseId: course.id }))} activeUnitId={activeUnitId} onSelect={onSelect} onDragChange={onDragChange} />
        </section>
      ))}
    </div>
  );
}
